import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ContentTypeEnum } from 'app/library/widgets/models/content-type.enum';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MaxbrainImage } from '../../image-library/models/maxbrain-image.model';
import { IEngageCategoryFilters } from '../interfaces/engage-category-filters.interface';
import { IEngageCateogoryWithPostingQuery } from '../interfaces/interfaces/engage-category-with-posting.query.interface';
import { IEngageCateogoryQuery } from '../interfaces/interfaces/engage-category.query.interface';
import { IPostingQuery } from '../interfaces/interfaces/posting.query.interface';
import { EditPostingFromArticleCommand } from '../models/edit-posting-from-article.command.model';
import { EngageCategoryWithPosting } from '../models/engage-category-with-posting.model';
import { EngageCategory } from '../models/engage-category.model';
import { PostingFromArticleCommand } from '../models/posting-from-article.command.model';
import { PostingImageCommand } from '../models/posting-image.command.model';
import { PostingCommand } from '../models/posting.command.model';
import { Posting } from '../models/posting.model';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

@Injectable()
export class PostingApiService extends EntityService<IPostingQuery, PostingCommand> implements IEntityApiService<Posting> {
    postingsPage = 1;

    private _postingsPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get postingsPagination(): Pagination {
        return this._postingsPagination;
    }
    set postingsPagination(value: Pagination) {
        this._postingsPagination = value;
    }

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'engage/postings');
    }

    create(entity: Posting): Observable<Posting> {
        const postingData = new PostingCommand(entity);

        return this._create(postingData).pipe(map((postingQuery) => new Posting(postingQuery)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    read(id: string): Observable<Posting> {
        return this.http.get<IPostingQuery>(`${this.apiUrl}/${this.entities}/${id}`).pipe(map((query) => new Posting(query)));
    }

    update(entity: Posting): Observable<Posting> {
        let data: EditPostingFromArticleCommand | PostingCommand;
        if (entity.kind === 'article') {
            data = new EditPostingFromArticleCommand(entity);
        } else {
            data = new PostingCommand(entity);
        }
        return this.http.patch<IPostingQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new Posting(query)));
    }

    createPostingFromArticle(posting: Posting): Observable<Posting> {
        const data = new PostingFromArticleCommand(posting);
        return this.http.post<IPostingQuery>(`${this.apiUrl}/engage/postings/from-article`, data).pipe(map((postingQuery) => new Posting(postingQuery)));
    }

    updatePostingFromArticle(posting: Posting): Observable<Posting> {
        const data = new EditPostingFromArticleCommand(posting);
        return this.http.patch<IPostingQuery>(`${this.apiUrl}/${this.entities}/${posting.id}`, data).pipe(map((postingQuery) => new Posting(postingQuery)));
    }

    getAll(): Observable<Posting[]> {
        return this.http.get<IPostingQuery[]>(`${this.apiUrl}/engage/postings`).pipe(
            map((queries) =>
                queries.map((query) => {
                    return new Posting(query);
                })
            )
        );
    }

    fetchEngageCategories(audienceId: string, searchTerm: string, postingTypeId: number, filters?: IEngageCategoryFilters): Observable<EngageCategory[]> {
        let params = new HttpParams();

        if (audienceId) {
            params = params.set('filter[audience]', audienceId);
        }

        if (searchTerm) {
            params = params.set('filter[query]', searchTerm);
        }

        if (postingTypeId) {
            params = params.set('filter[type]', postingTypeId.toString());
        }

        params = this._appendEngageCategoryFilters(params, filters);

        return this.http.get<IEngageCateogoryQuery[]>(`${this.apiUrl}/engage/categories`, { params }).pipe(
            map((queries) =>
                queries.map((query) => {
                    return new EngageCategory(query);
                })
            )
        );
    }

    fetchEngageCategoriesWithPostings(audienceId: string, filters?: IEngageCategoryFilters): Observable<EngageCategoryWithPosting[]> {
        let params = new HttpParams();

        if (audienceId) {
            params = params.set('filter[audience]', audienceId);
        }

        params = this._appendEngageCategoryFilters(params, filters);

        return this.http.get<IEngageCateogoryWithPostingQuery[]>(`${this.apiUrl}/engage/categories-with-postings`, { params }).pipe(
            map((queries) =>
                queries.map((query) => {
                    return new EngageCategoryWithPosting(query);
                })
            )
        );
    }

    private _appendEngageCategoryFilters(params: HttpParams, filters?: IEngageCategoryFilters): HttpParams {
        if (filters?.sectionId) {
            params = params.set('filter[sectionId]', filters.sectionId);
        }

        if (filters?.courseOfferingId) {
            params = params.set('filter[courseOfferingId]', filters.courseOfferingId);
        }

        return params;
    }
    /**
     *
     * @param search
     * @param sortBy
     * @param sortOrder
     * @param sortLanguage
     * @param pageSize
     * @param pageIndex
     */
    fetchPostings(
        sortBy = '',
        sortOrder = '',
        pageSize = 5,
        pageIndex = 1,
        search = '',
        postingType?: number,
        languageId?: string,
        contentTypeId?: number,
        onlyValid?: boolean
    ): Observable<IPaginatedResponse<IPostingQuery>> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy) {
            params = params.set('sortBy', sortBy);
        }

        if (sortOrder) {
            params = params.set('sortOrder', sortOrder);
        }

        if (postingType) {
            params = params.set('filter[type]', postingType.toString());
        }

        if (contentTypeId === ContentTypeEnum.Posting) {
            params = params.set('filter[kind]', 'normal');
        } else if (contentTypeId === ContentTypeEnum.Article) {
            params = params.set('filter[kind]', 'article');
        }

        if (languageId) {
            params = params.set('filter[language]', languageId);
        }

        if (onlyValid) {
            params = params.set('filter[onlyValid]', 'true');
        }

        return this.http.get<IPaginatedResponse<IPostingQuery>>(`${this.apiUrl}/engage/postings`, { params }).pipe(
            map((queries) => {
                this.postingsPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                return queries;
            })
        );
    }

    /**
     *
     * @param postingId
     */
    removePostingImage(postingId: string): Observable<MaxbrainImage> {
        const data = {
            image: new PostingImageCommand(),
        };

        return this.http.patch<IPostingQuery>(`${this.apiUrl}/engage/postings/${postingId}`, data).pipe(map((query) => new MaxbrainImage(query.image)));
    }

    /**
     * Method to add Image to entity Posting
     * @param postingId
     * @param image
     */
    addPostingImage(postingId: string, image: MaxbrainImage): Observable<MaxbrainImage> {
        const data = {
            image: new PostingImageCommand(null, image),
        };
        return this.http.patch<IPostingQuery>(`${this.apiUrl}/engage/postings/${postingId}`, data).pipe(map((query) => new MaxbrainImage(query.image)));
    }
}
